import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import axios from "axios";
import AdminNavbar from "../Layout/AdminNavbar";
import { FaTrash } from "react-icons/fa";
import API_BASE_URL from "../config";
import "./AdminManageTeam.css";

/* ────────────────────────────────────────────
   Custom Searchable Dropdown
──────────────────────────────────────────── */
const CustomSelect = ({ value, onChange, options, placeholder }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [coords, setCoords] = useState({});
  const triggerRef = useRef(null);
  const inputRef = useRef(null);

  const selected = options.find((o) => String(o.value) === String(value));

  const recalc = () => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom;
    const openUp = spaceBelow < 270 && r.top > 270;
    setCoords({
      left: r.left,
      width: r.width,
      ...(openUp ? { bottom: window.innerHeight - r.top + 5 } : { top: r.bottom + 5 }),
    });
  };

  const handleOpen = () => {
    recalc();
    setOpen(true);
  };

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        !e.target.closest(".csel-panel")
      ) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Focus search input after open
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const filtered = options.filter((o) =>
    o.label.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = (opt) => {
    onChange(opt.value);
    setOpen(false);
    setSearch("");
  };

  const panel = open ? (
    <div
      className="csel-panel"
      style={{ position: "fixed", zIndex: 99999, ...coords }}
    >
      <div className="csel-search-wrap">
        <svg className="csel-search-icon" width="13" height="13" viewBox="0 0 13 13" fill="none">
          <circle cx="5.5" cy="5.5" r="4" stroke="#A8A29E" strokeWidth="1.4" />
          <path d="M9 9l2.5 2.5" stroke="#A8A29E" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
        <input
          ref={inputRef}
          className="csel-search"
          placeholder="Search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <ul className="csel-list">
        {filtered.length === 0 ? (
          <li className="csel-empty">No results</li>
        ) : (
          filtered.map((opt) => (
            <li
              key={opt.value}
              className={`csel-option ${String(opt.value) === String(value) ? "csel-option--active" : ""}`}
              onMouseDown={() => handleSelect(opt)}
            >
              <span className="csel-option-label">{opt.label}</span>
              {String(opt.value) === String(value) && (
                <svg className="csel-check" width="13" height="13" viewBox="0 0 13 13" fill="none">
                  <path d="M2 6.5l3.5 3.5 5.5-6" stroke="currentColor"
                    strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </li>
          ))
        )}
      </ul>
    </div>
  ) : null;

  return (
    <div className="csel-root">
      <button
        ref={triggerRef}
        type="button"
        className={`csel-trigger ${open ? "csel-trigger--open" : ""}`}
        onClick={() => (open ? (setOpen(false), setSearch("")) : handleOpen())}
      >
        <span className={`csel-trigger-label ${!selected ? "csel-placeholder" : ""}`}>
          {selected ? selected.label : placeholder}
        </span>
        <svg
          className={`csel-chevron ${open ? "csel-chevron--up" : ""}`}
          width="14" height="14" viewBox="0 0 14 14" fill="none"
        >
          <path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth="1.5"
            strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {createPortal(panel, document.body)}
    </div>
  );
};

/* ────────────────────────────────────────────
   Main Component
──────────────────────────────────────────── */
const AdminManageTeams = () => {
  const [myTeam, setMyTeam] = useState([]);
  const [teams, setTeams] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);

  const [filters, setFilters] = useState(() => {
    const savedFilters = localStorage.getItem("manageTeamFilters");
    return savedFilters ? JSON.parse(savedFilters) : { teamId: "", projectId: "" };
  });

  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });

  const [formData, setFormData] = useState({
    teamId: "", employeeId: "", reporterId: "", projectId: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        const axiosConfig = { headers: { Authorization: `Bearer ${token}` } };
        const [teamRes, projRes, myTeamRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/teams`, axiosConfig),
          axios.get(`${API_BASE_URL}/api/projects/all`, axiosConfig),
          axios.get(`${API_BASE_URL}/api/manageteam`, axiosConfig),
        ]);
        setTeams(teamRes.data);
        setProjects(projRes.data);
        setMyTeam(Array.isArray(myTeamRes.data) ? myTeamRes.data : []);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load data.");
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (!showForm) return;
    const fetchEmployees = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API_BASE_URL}/api/employees/active`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setEmployees(res.data);
      } catch (err) {
        toast.error("Failed to load employees.");
      }
    };
    fetchEmployees();
  }, [showForm]);

  useEffect(() => {
    localStorage.setItem("manageTeamFilters", JSON.stringify(filters));
  }, [filters]);

  const handleFilterChange = (e) =>
    setFilters({ ...filters, [e.target.name]: e.target.value });

  const setFormField = (name, val) =>
    setFormData((prev) => ({ ...prev, [name]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.teamId || !formData.projectId || !formData.employeeId || !formData.reporterId) {
      toast.error("All fields are required.");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const config = {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      };
      const response = await axios.post(`${API_BASE_URL}/api/manageteam`, formData, config);
      setMyTeam((prev) => [...prev, response.data]);
      toast.success("Team Member added successfully!");
      setFormData({ teamId: "", employeeId: "", reporterId: "", projectId: "" });
      setShowForm(false);
    } catch (err) {
      toast.error(err.response?.data || "Something went wrong.");
    }
  };

  const confirmDelete = async () => {
    if (!selectedMember) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_BASE_URL}/api/manageteam/${selectedMember.assignmentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMyTeam((prev) => prev.filter((m) => m.assignmentId !== selectedMember.assignmentId));
      toast.success(`${selectedMember.employeeFirstName} ${selectedMember.employeeLastName} removed successfully`);
      setShowDeleteModal(false);
      setSelectedMember(null);
    } catch (err) {
      toast.error("Error removing member.");
    }
  };

  let filteredTeam = myTeam.filter((m) => {
    const teamMatch = !filters.teamId || Number(m.teamId) === Number(filters.teamId);
    const projectMatch = !filters.projectId || m.projectName === filters.projectId;
    return teamMatch && projectMatch;
  });

  const sortData = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const getValue = (m, key) => {
    switch (key) {
      case "employee": return m.employeeFirstName ?? "";
      case "team": return m.teamName ?? "";
      case "project": return m.projectName ?? "";
      case "reporter": return m.reporterFirstName ?? "";
      case "id": return m.assignmentId ?? "";
      default: return "";
    }
  };

  if (sortConfig.key) {
    filteredTeam = [...filteredTeam].sort((a, b) => {
      const valA = getValue(a, sortConfig.key);
      const valB = getValue(b, sortConfig.key);
      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }

  const arrow = (key) =>
    sortConfig.key === key ? (sortConfig.direction === "asc" ? " ↑" : " ↓") : "";

  const closeModal = () => {
    setFormData({ teamId: "", projectId: "", employeeId: "", reporterId: "" });
    setShowForm(false);
  };

  const initials = (first, last) =>
    `${(first?.[0] || "").toUpperCase()}${(last?.[0] || "").toUpperCase()}`;

  const teamOptions = [...teams]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((t) => ({ value: t.id, label: t.name }));

  const projectOptions = [...projects]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((p) => ({ value: p.id, label: p.name }));

  const employeeOptions = employees.map((e) => ({
    value: e.id,
    label: `${e.firstName} ${e.lastName}`,
  }));

  return (
    <div className="amt-root">
      <AdminNavbar />

      <div className="amt-page">

        {/* Header */}
        <div className="amt-page-header">
          <div>
            <h1 className="amt-page-title">Manage Team</h1>
            <p className="amt-page-sub">
              {filteredTeam.length} member{filteredTeam.length !== 1 ? "s" : ""} shown
            </p>
          </div>
          <button
            className="amt-btn-primary"
            onClick={() => {
              setFormData((prev) => ({
                ...prev,
                teamId: filters.teamId || "",
                projectId: filters.projectId
                  ? projects.find((p) => p.name === filters.projectId)?.id || ""
                  : "",
              }));
              setShowForm(true);
            }}
          >
            <span className="amt-btn-icon">+</span>
            Add
          </button>
        </div>

        {/* Filters */}
        <div className="amt-filters">
          <div className="amt-filter-group">
            <label className="amt-filter-label">Team</label>
            <select name="teamId" value={filters.teamId} onChange={handleFilterChange} className="amt-select">
              <option value="">All Teams</option>
              {teamOptions.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          <div className="amt-filter-group">
            <label className="amt-filter-label">Project</label>
            <select name="projectId" value={filters.projectId || ""} onChange={handleFilterChange} className="amt-select">
              <option value="">All Projects</option>
              {[...projects].sort((a, b) => a.name.localeCompare(b.name)).map((p) => (
                <option key={p.id} value={p.name}>{p.name}</option>
              ))}
            </select>
          </div>

          {(filters.teamId || filters.projectId) && (
            <button className="amt-clear-btn" onClick={() => setFilters({ teamId: "", projectId: "" })}>
              ✕ Clear filters
            </button>
          )}
        </div>

        {/* Table */}
        <div className="amt-table-card">
          {filteredTeam.length === 0 ? (
            <div className="amt-empty">
              <div className="amt-empty-icon">👥</div>
              <p className="amt-empty-title">No team members found</p>
              <p className="amt-empty-sub">Try adjusting your filters or add a new member.</p>
            </div>
          ) : (
            <div className="amt-table-wrap">
              <table className="amt-table">
                <thead>
                  <tr>
                    {[
                      { key: "id", label: "Sr.no" },
                      { key: "employee", label: "Employee" },
                      { key: "team", label: "Team" },
                      { key: "project", label: "Project" },
                      { key: "reporter", label: "Manager" },
                    ].map(({ key, label }) => (
                      <th key={key} onClick={() => sortData(key)} className="amt-th sortable">
                        {label}
                        <span className="amt-sort-arrow">
                          {arrow(key) || <span className="amt-sort-hint">⇅</span>}
                        </span>
                      </th>
                    ))}
                    <th className="amt-th">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTeam.map((m, idx) => (
                    <tr key={m.assignmentId} className="amt-tr">
                      <td className="amt-td amt-td-num">{idx + 1}</td>
                      <td className="amt-td">
                        <div className="amt-employee-cell">
                          <div className="amt-avatar">
                            {m.employeePhotoPath ? (
                              <img
                                src={`${API_BASE_URL}/uploads/${m.employeePhotoPath}`}
                                alt={`${m.employeeFirstName} ${m.employeeLastName}`}
                                className="amt-avatar-img"
                              />
                            ) : (
                              initials(m.employeeFirstName, m.employeeLastName)
                            )}
                          </div>
                          <span className="amt-employee-name">
                            {m.employeeFirstName} {m.employeeLastName}
                          </span>
                        </div>
                      </td>
                      <td className="amt-td">
                        <span className="amt-badge amt-badge-team">{m.teamName}</span>
                      </td>
                      <td className="amt-td">
                        <span className="amt-badge amt-badge-project">{m.projectName}</span>
                      </td>
                      <td className="amt-td amt-td-reporter">
                        {m.reporterFirstName} {m.reporterLastName}
                      </td>
                      <td className="amt-td">
                        <button
                          className="amt-delete-btn"
                          title="Remove member"
                          onClick={() => { setSelectedMember(m); setShowDeleteModal(true); }}
                        >
                          <FaTrash size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add Modal */}
      {showForm && (
        <>
          <div className="amt-backdrop" onClick={closeModal} />
          <div className="amt-modal-wrap">
            <div className="amt-modal">
              <div className="amt-modal-header">
                <div>
                  <h2 className="amt-modal-title">Add Team Member</h2>
                  <p className="amt-modal-sub">Fill in the details to assign a member.</p>
                </div>
                <button className="amt-modal-close" onClick={closeModal}>✕</button>
              </div>

              <div className="amt-modal-body">
                <div className="amt-form-grid">

                  <div className="amt-form-field">
                    <label className="amt-form-label">Team <span className="amt-required">*</span></label>
                    <CustomSelect
                      value={formData.teamId}
                      onChange={(v) => setFormField("teamId", v)}
                      options={teamOptions}
                      placeholder="Select Team"
                    />
                  </div>

                  <div className="amt-form-field">
                    <label className="amt-form-label">Project <span className="amt-required">*</span></label>
                    <CustomSelect
                      value={formData.projectId}
                      onChange={(v) => setFormField("projectId", v)}
                      options={projectOptions}
                      placeholder="Select Project"
                    />
                  </div>

                  <div className="amt-form-field">
                    <label className="amt-form-label">Employee <span className="amt-required">*</span></label>
                    <CustomSelect
                      value={formData.employeeId}
                      onChange={(v) => setFormField("employeeId", v)}
                      options={employeeOptions}
                      placeholder="Select Employee"
                    />
                  </div>

                  <div className="amt-form-field">
                    <label className="amt-form-label">Reporter / Manager <span className="amt-required">*</span></label>
                    <CustomSelect
                      value={formData.reporterId}
                      onChange={(v) => setFormField("reporterId", v)}
                      options={employeeOptions}
                      placeholder="Select Reporter"
                    />
                  </div>

                </div>
              </div>

              <div className="amt-modal-footer">
                <button className="amt-btn-primary" onClick={handleSubmit}>Save</button>
                <button className="amt-btn-ghost" onClick={closeModal}>Cancel</button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Delete Confirm Modal */}
      {showDeleteModal && selectedMember && (
        <>
          <div className="amt-backdrop" onClick={() => setShowDeleteModal(false)} />
          <div className="amt-modal-wrap">
            <div className="amt-modal amt-modal-sm">
              <div className="amt-modal-header">
                <div>
                  <h2 className="amt-modal-title amt-modal-title-danger">Remove Member</h2>
                  <p className="amt-modal-sub">This action cannot be undone.</p>
                </div>
                <button className="amt-modal-close" onClick={() => setShowDeleteModal(false)}>✕</button>
              </div>

              <div className="amt-modal-body">
                <div className="amt-confirm-card">
                  <div className="amt-confirm-avatar">
                    {selectedMember.employeePhotoPath ? (
                      <img
                        src={`${API_BASE_URL}/uploads/${selectedMember.employeePhotoPath}`}
                        alt={`${selectedMember.employeeFirstName} ${selectedMember.employeeLastName}`}
                        className="amt-avatar-img"
                      />
                    ) : (
                      initials(selectedMember.employeeFirstName, selectedMember.employeeLastName)
                    )}
                  </div>
                  <div className="amt-confirm-info">
                    <p className="amt-confirm-name">
                      {selectedMember.employeeFirstName} {selectedMember.employeeLastName}
                    </p>
                    <p className="amt-confirm-meta">
                      {selectedMember.teamName} · {selectedMember.projectName}
                    </p>
                    <p className="amt-confirm-meta">
                      Reports to: {selectedMember.reporterFirstName} {selectedMember.reporterLastName}
                    </p>
                  </div>
                </div>
              </div>

              <div className="amt-modal-footer">
                <button className="amt-btn-danger" onClick={confirmDelete}>Remove</button>
                <button className="amt-btn-ghost" onClick={() => setShowDeleteModal(false)}>Cancel</button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminManageTeams;